import { useMemo } from 'react';

import { MemberCountry } from '@data/utils/types';
import type { ProductEntity } from '@data/utils/types';

import { effectiveChapter, effectiveSeniority, loadProductChapters, loadProductSeniority } from '../productMock';
import type { VersionTeam } from './budget';
import { COST_COUNTRIES, readCostMatrix } from './costs';
import type { ProductVersion } from './productVersions';
import { readPricing } from './pricing';
import { onVersionTeam, readAddedTo } from './versionTeam';

const nameOf = (person: { firstName?: string; lastName?: string }): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();

/** Priced off the team the product itself reports, so a client's page prices exactly as ours does. */
export const versionTeams = (product: ProductEntity | undefined, versions: ProductVersion[]): VersionTeam[] =>
  versions.map(version => {
    const levels = loadProductSeniority(product?.id ?? '', version.number);
    const chapters = loadProductChapters(product?.id ?? '', version.number);
    const added = readAddedTo(product?.id ?? '', version.number);
    return {
      version,
      teamIds: (product?.team ?? [])
        .filter(member => onVersionTeam(member, version, added))
        .map(member => member.membershipId),
      people: (product?.team ?? []).map(member => ({
        id: member.membershipId,
        name: nameOf(member),
        chapterKey: effectiveChapter(chapters[member.membershipId], member.chapterKey ?? ''),
        level: effectiveSeniority(levels[member.membershipId], member.seniority ?? ''),
        // An unset country prices as Belgium, as the matrix does.
        country: member.country ?? MemberCountry.belgium,
        hourlyCost: member.hourlyCost ?? null,
      })),
      pricing: readPricing(product?.id ?? '', version.number),
      matrices: Object.fromEntries(
        COST_COUNTRIES.map(country => [country, readCostMatrix(product?.id ?? '', country, version.number)]),
      ),
    };
  });

export const useVersionTeams = (
  product: ProductEntity | undefined,
  versions: ProductVersion[],
  /** Changes whenever the stores behind a team may have moved: no query cache watches the product store. */
  read: string,
): VersionTeam[] =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => versionTeams(product, versions), [product?.id, product?.team, versions, read]);

export default useVersionTeams;
